(function () {
  "use strict";

  /* ---- Scroll header: TWO states, chosen by scroll POSITION alone.

     Resting state, at the top of the page:
       Home            transparent, big centered logo, floating over the hero
       every other page solid black, big centered logo

     Collapsed state, past the threshold: the compact Converse-style bar
     (is-compact), on every template alike.

     The header is ALWAYS visible — this only ever switches which LAYOUT
     it's in, it never hides or fades away.

     WHAT CHANGED, AND WHY IT MATTERS. This used to be direction-driven:
     scrolling down collapsed the header, scrolling up expanded it again
     immediately, anywhere on the page. Two things fell out of that. The
     tall big-logo layout kept reappearing mid-page on Home, where it was
     never meant to be seen — that is the "third nav" the owner reported.
     And because it took 116px of vertical space with it every time, a
     small upward nudge could shove the content the reader was looking at
     down most of a phone screen. Direction is now not an input at all:
     past the threshold you are collapsed, and the only way back to the
     resting state is returning to the top of the page.

     Home vs. every other page now differ ONLY in what the resting state
     looks like. Every other template has its background forced opaque by
     CSS regardless of class (body:not(.template-index) .site-header__row
     in theme.css.liquid), so "transparent" is not a state they can
     meaningfully be in.

     Self-healing by construction: every tick derives the state from the
     CURRENT scrollY and nothing else — no accumulated delta, no
     last-known-direction — so the header cannot get stuck in a stale
     state regardless of what happened on the way there. */
  var header = document.querySelector("[data-site-header]");
  var isHome = document.body.classList.contains("template-index");

  /* ---- Reserve space for the fixed header on every page except Home.
     The header is position:fixed (so it can float transparently over the
     Home hero), which means on every OTHER template the page content was
     starting at y=0 underneath it, with the header's opaque background
     covering the top of the first section. Measuring the header's real
     rendered height (it varies with the promo bar, logo height setting,
     and viewport width) and pushing that into a CSS variable lets
     non-Home templates reserve exactly that much space at the top instead
     of guessing a fixed pixel value that would drift out of sync. */
  /* ALWAYS measure the EXPANDED (is-solid) height, never the compact one.
     This function runs on load and on resize, and it used to measure
     whatever state the header happened to be in at that instant. The two
     states differ by 116px (203px expanded vs 87px compact -- the big logo
     is swapped for the small one). So if a resize fired while the visitor
     was scrolled down -- a window drag, DevTools opening, an orientation
     change, or mobile Safari's URL bar collapsing, which fires resize on
     essentially every scroll -- the variable got baked at 87px. Scrolling
     back up then re-expanded the header to 203px over content that was
     only reserving 87px, so the header covered the top 116px of the page
     until the next resize happened to occur at the top.

     Content has to clear the header in its TALLEST state, so the compact
     class is lifted for the measurement and restored in the same
     synchronous task -- reading offsetHeight forces layout, but no paint
     happens in between, so there's no visible flash. */
  /* --header-height is the height of the header CHROME -- announcement bar
     plus the logo/icon row. It is NOT header.offsetHeight.

     It used to be, and that was a feedback loop with teeth. The mobile
     panel is a child of <header>, so once the menu opens, header.offsetHeight
     includes the open panel. Meanwhile the panel sizes itself from
     calc(100dvh - var(--header-height)). So:

       menu opens        panel ~600px tall
       any resize fires  (a phone URL bar collapsing counts as one)
       remeasure         --header-height := chrome 120 + panel 600 = 720
       panel restyles    100dvh - 720  =  ~60px  -- panel collapses
       ...and it can settle anywhere on the way, differently every time.

     That is the whole of the reported symptom: the menu changing height
     while scrolling, cutting off at a different link each time, and the
     tail being unreachable. Not a scroll bug, not a dvh bug -- the panel
     was being measured as part of its own input.

     Measuring to the panel's top edge instead is immune to it: whatever
     sits above the panel is exactly the chrome, however the header is
     restructured later. Falls back to offsetHeight only when there is no
     panel to measure to (desktop-only markup), where the loop can't exist. */
  function setHeaderHeightVar() {
    if (!header) return;
    var wasCompact = header.classList.contains("is-compact");
    if (wasCompact) header.classList.remove("is-compact");

    var panel = header.querySelector("[data-mobile-panel]");
    var chromeHeight;
    // The panel is display:none above 1280px, where getBoundingClientRect()
    // returns all zeros -- which measured as a chrome height of 0 and tripped
    // the sanity check below, so --header-height was never published on
    // desktop at all and every consumer silently fell back to 120px. Above
    // the breakpoint there is no panel in the box anyway, so offsetHeight is
    // both correct and immune to the feedback loop described above.
    if (panel && panel.offsetParent !== null) {
      chromeHeight = panel.getBoundingClientRect().top - header.getBoundingClientRect().top;
    } else {
      chromeHeight = header.offsetHeight;
    }
    if (wasCompact) header.classList.add("is-compact");

    // A subpixel or transient-zero read would propagate into every consumer
    // of the variable, so ignore anything that isn't plausible chrome.
    if (!(chromeHeight > 0)) return;
    document.documentElement.style.setProperty("--header-height", Math.round(chromeHeight) + "px");
  }
  setHeaderHeightVar();
  window.addEventListener("load", setHeaderHeightVar);
  var headerResizeTimer = null;
  window.addEventListener("resize", function () {
    clearTimeout(headerResizeTimer);
    headerResizeTimer = setTimeout(setHeaderHeightVar, 150);
  });

  if (header) {
    // Home rests transparent (floating over the hero, which is a full
    // viewport tall at every breakpoint). Every other template rests solid.
    var allowTransparent = isHome;

    /* TWO thresholds, deliberately far apart, not one.

       A single threshold flickers: park the page exactly on it and every
       ±1px of elastic overscroll, trackpad momentum tail, or Magic Mouse
       touch-surface noise flips between two layouts that differ by 116px
       in height. The old direction-driven code fought that with an 8px
       net-travel filter on the DELTA. With direction gone, a 60px dead
       band between "collapse" and "restore" does the same job
       structurally, and unlike an accumulated delta it has no state that
       can drift out of sync.

       Collapse only past 120px, because near the top the expanded header
       is the whole visual identity of the page and collapsing it there is
       the most jarring place for it to happen. Restore at 60px rather
       than at a literal y=0 so the handoff has a little runway under it
       instead of snapping on the final pixel of an upward scroll. */
    /* Both thresholds shrink on a short viewport, and the reason is that
       120px means something completely different depending on how tall the
       screen is. On a 900px portrait phone it is a seventh of a screenful --
       a deliberate scroll. On a 427px landscape phone it is 28% of
       everything visible, so the tall header sits there eating a third of
       the screen through most of the first swipe. Measured at 956x427: 143px
       of header at rest against 72px once compact, so getting there sooner
       is worth 71px on a screen that has none to spare.

       Read per tick rather than captured once, so rotating the device
       switches thresholds without needing a resize listener of its own. */
    function collapseAt() { return window.innerHeight < 500 ? 40 : 120; }
    function restoreAt() { return window.innerHeight < 500 ? 20 : 60; }

    var isCompactState = null; // null until the first tick resolves it
    var ticking = false;

    function applyHeaderState(compact) {
      if (compact === isCompactState) return; // no redundant class churn
      isCompactState = compact;
      if (compact) {
        header.classList.remove("is-solid");
        header.classList.add("is-compact");
      } else {
        header.classList.remove("is-compact");
        if (allowTransparent) {
          header.classList.remove("is-solid");
        } else {
          header.classList.add("is-solid");
        }
      }
    }

    function onScroll() {
      ticking = false;

      /* The mobile menu freezes the body with position:fixed, which makes
         scrollY read 0. Without this guard, opening the menu while
         scrolled down would expand the header out of its compact state --
         moving the panel's top edge down by 116px while the panel keeps
         the height it was given, pushing its tail off the bottom of the
         screen. Hold the header exactly as it is until the body is
         released; unlockBodyScroll's scrollTo fires a scroll event that
         re-resolves it correctly on the way out. */
      if (document.body.style.position === "fixed") return;

      var y = window.scrollY;

      if (y >= collapseAt()) {
        applyHeaderState(true);
      } else if (y <= restoreAt() || isCompactState === null) {
        /* isCompactState === null is the very first tick. A page can load
           already scrolled -- an anchor link, or the browser restoring
           position on back/forward -- and land inside the dead band, where
           there is no previous state to hold on to. Resolve that as
           resting: the band exists only to stop flicker BETWEEN two known
           states, never to be an initial state of its own. */
        applyHeaderState(false);
      }
      /* Between restoreAt() and collapseAt(): deliberately do nothing
         and hold the current state. That gap is the hysteresis. */
    }

    window.addEventListener(
      "scroll",
      function () {
        if (!ticking) {
          window.requestAnimationFrame(onScroll);
          ticking = true;
        }
      },
      { passive: true }
    );
    onScroll();
  }

  /* ---- Hero headline: fit each sentence on one line, measured on the
     device rather than predicted from units.

     WHY THIS EXISTS, given the CSS already does it. The stylesheet sizes
     the headline from the container (100cqw) with the viewport as a second
     opinion, and that is verified correct: real hero markup, real served
     CSS, a real 430px viewport gives 33.2px and one line per sentence.
     The same stylesheet on the owner's phone produced roughly 44px and six
     lines, repeatedly, across three browsers and several deploys.

     Every explanation that survived checking was a unit resolving to
     something other than the visible width. This function does not use
     units at all. It measures what the browser actually rendered, on the
     device that rendered it, and sets a pixel size from that. It also
     covers browsers with no @container support (Safari before 16), where
     the CSS silently falls back to the viewport rule.

     Inline styles win over every stylesheet rule, so whatever the cascade
     was doing, this is the last word.

     Deliberately NOT white-space:nowrap anywhere: if a merchant writes a
     longer sentence it should wrap untidily, never run off the screen. */
  var heroContent = document.querySelector(".hero .hero__content");
  if (heroContent) {
    var HERO_MAX_PX = 44;
    var HERO_HEADROOM = 1.03; // ~3%, same margin the CSS divisor carries
    var heroFitTicking = false;

    function fitHeroHeadline() {
      var heads = heroContent.querySelectorAll(".hx-heading--xl");
      if (!heads.length) return;

      var cs = window.getComputedStyle(heroContent);
      var padX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);

      /* The smaller of the container box and the viewport box. If either is
         lying -- an over-wide container, or a viewport that reports wider
         than the screen -- the other one still bounds the answer. */
      var byContainer = heroContent.clientWidth - padX;
      var byViewport = document.documentElement.clientWidth - padX;
      var avail = Math.min(byContainer, byViewport);
      if (!(avail > 0)) return;

      // Desktop keeps its own type scale; hand control back to the CSS.
      if (avail > 700) {
        for (var d = 0; d < heads.length; d++) heads[d].style.fontSize = "";
        return;
      }

      /* Measure the widest sentence as a ratio of its own font-size, using
         a clone so the live headline never flashes at the probe size. The
         clone goes inside the same parent, so it inherits family, weight,
         letter-spacing and text-transform without having to copy them. */
      var widest = 0;
      for (var i = 0; i < heads.length; i++) {
        var probe = heads[i].cloneNode(true);
        probe.style.cssText =
          "position:absolute;visibility:hidden;pointer-events:none;" +
          "white-space:nowrap;width:auto;max-width:none;font-size:100px;";

        /* A rotating word must be measured at its WIDEST, not at whichever
           word happens to be showing. The stack's words are absolutely
           positioned and word-rotate.js sets the wrapper's width inline to
           the active one, so a naive clone measures "Dreamers" (223px at
           62px type) and sizes the headline for that -- then the line wraps
           the moment "Entrepreneurs" (338px) arrives. Intermittent, and it
           would look like a random reflow rather than a sizing bug.

           Laying every word out in flow makes the clone as wide as the
           longest one, which is the only width that is safe for all five. */
        var stack = probe.querySelector(".word-rotate");
        if (stack) {
          stack.style.width = "auto";
          var stackWords = probe.querySelectorAll(".word-rotate__word");
          for (var s = 0; s < stackWords.length; s++) {
            stackWords[s].style.position = "static";
            stackWords[s].style.opacity = "1";
            /* Only the widest should contribute; the rest collapse to zero
               so they do not sum into one absurdly long line. */
            stackWords[s].style.display = "inline-block";
          }
        }

        heads[i].parentNode.appendChild(probe);
        if (stack) {
          /* Now that they are laid out, keep the widest and drop the rest. */
          var laid = probe.querySelectorAll(".word-rotate__word");
          var widest = null;
          for (var q = 0; q < laid.length; q++) {
            if (!widest || laid[q].getBoundingClientRect().width > widest.getBoundingClientRect().width) {
              widest = laid[q];
            }
          }
          for (var z = 0; z < laid.length; z++) {
            if (laid[z] !== widest) laid[z].style.display = "none";
          }
        }
        var ratio = probe.getBoundingClientRect().width / 100;
        probe.parentNode.removeChild(probe);
        if (ratio > widest) widest = ratio;
      }
      if (!(widest > 0)) return;

      var size = Math.min(avail / (widest * HERO_HEADROOM), HERO_MAX_PX);
      for (var j = 0; j < heads.length; j++) {
        heads[j].style.fontSize = size.toFixed(2) + "px";
      }
    }

    fitHeroHeadline();
    /* Web fonts change glyph widths, so a measurement taken against the
       fallback face would be wrong by however much the two differ. */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(fitHeroHeadline).catch(function () {});
    }
    window.addEventListener("load", fitHeroHeadline);
    window.addEventListener("orientationchange", function () {
      window.setTimeout(fitHeroHeadline, 120);
    });
    window.addEventListener("resize", function () {
      if (heroFitTicking) return;
      heroFitTicking = true;
      window.requestAnimationFrame(function () {
        fitHeroHeadline();
        heroFitTicking = false;
      });
    });
  }

  /* ---- Announcement/promo bar: Home page only (sections/header.liquid
     doesn't even render the markup on other templates, so this element
     just won't exist there and the block below no-ops). On Home, visible
     only at the absolute top of the page, hides on any scroll away from
     y=0, independent of the header's own logic above and of scroll
     direction. ---- */
  var announcementBar = document.querySelector("[data-announcement-bar]");
  if (announcementBar) {
    var annTicking = false;
    function onAnnouncementScroll() {
      var y = window.scrollY;
      announcementBar.classList.toggle("is-hidden", y > 4);
      annTicking = false;
    }
    window.addEventListener(
      "scroll",
      function () {
        if (!annTicking) {
          window.requestAnimationFrame(onAnnouncementScroll);
          annTicking = true;
        }
      },
      { passive: true }
    );
    onAnnouncementScroll();
  }

  /* ---- Announcement carousel: auto-rotate between multiple messages, with
     manual chevrons to page through them. Only active once a merchant adds
     more than one announcement block in the theme editor — with a single
     block the chevrons aren't even rendered and this is a no-op. ---- */
  var carousel = document.querySelector("[data-announcement-carousel]");
  if (carousel) {
    var slides = carousel.querySelectorAll(".announcement-bar__slide");

    if (slides.length > 1) {
      var activeIndex = 0;
      var rotateTimer = null;
      var isPaused = false;

      function showAnnouncementSlide(index) {
        slides[activeIndex].classList.remove("is-active");
        activeIndex = (index + slides.length) % slides.length;
        slides[activeIndex].classList.add("is-active");
      }

      function startAnnouncementRotation() {
        if (isPaused) return;
        rotateTimer = setInterval(function () {
          showAnnouncementSlide(activeIndex + 1);
        }, 5000);
      }

      function restartAnnouncementRotation() {
        if (rotateTimer) clearInterval(rotateTimer);
        startAnnouncementRotation();
      }

      startAnnouncementRotation();

      var prevBtn = document.querySelector("[data-announcement-prev]");
      var nextBtn = document.querySelector("[data-announcement-next]");
      var pauseBtn = document.querySelector("[data-announcement-pause]");
      if (prevBtn) {
        prevBtn.addEventListener("click", function () {
          showAnnouncementSlide(activeIndex - 1);
          restartAnnouncementRotation();
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener("click", function () {
          showAnnouncementSlide(activeIndex + 1);
          restartAnnouncementRotation();
        });
      }
      if (pauseBtn) {
        pauseBtn.addEventListener("click", function () {
          isPaused = !isPaused;
          pauseBtn.setAttribute("aria-pressed", isPaused ? "true" : "false");
          pauseBtn.setAttribute("aria-label", isPaused ? "Resume message rotation" : "Pause message rotation");
          pauseBtn.querySelector(".icon-pause").style.display = isPaused ? "none" : "";
          pauseBtn.querySelector(".icon-play").style.display = isPaused ? "" : "none";
          if (isPaused) {
            if (rotateTimer) clearInterval(rotateTimer);
          } else {
            restartAnnouncementRotation();
          }
        });
      }
    }
  }

  /* ---- Region/country selector in the promo bar. Swaps the flag + label,
     remembers the choice in localStorage, and drives Google's Website
     Translate widget (see the bottom of layout/theme.liquid) to actually
     machine-translate the page into the picked country's language.

     History, for whoever touches this next: this worked once, got pulled
     because Chrome's own native translate infobar fired on top of it, got
     re-added as a no-op (region indicator only), and is now restored a
     third time with the actual fix -- a <meta name="google"
     content="notranslate"> tag in <head> that tells Chrome to leave
     translation to us entirely instead of layering its own detection on
     top. If the infobar problem somehow comes back, that meta tag is the
     first thing to check, not this function. */
  var regionSelect = document.querySelector("[data-region-select]");
  if (regionSelect) {
    var REGION_KEY = "trulvl:region";
    var regionToggle = regionSelect.querySelector("[data-region-toggle]");
    var regionList = regionSelect.querySelector("[data-region-list]");
    var regionFlag = regionSelect.querySelector("[data-region-flag]");
    var regionLabel = regionSelect.querySelector("[data-region-label]");

    function applyRegion(flag, label) {
      if (regionFlag) regionFlag.textContent = flag;
      if (regionLabel) regionLabel.textContent = label;
    }

    function setCookie(name, value) {
      document.cookie = name + "=" + value + "; path=/";
      document.cookie = name + "=" + value + "; path=/; domain=" + window.location.hostname;
    }

    function clearCookie(name) {
      var expired = "; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/";
      document.cookie = name + "=" + expired;
      document.cookie = name + "=" + expired + "; domain=" + window.location.hostname;
    }

    /* Drives Google's widget via the documented (if unofficial) googtrans
       cookie + its hidden <select class="goog-te-combo">. If the widget
       hasn't finished loading yet, fall back to a reload so it picks up
       the cookie on init. */
    function setSiteLanguage(lang) {
      if (!lang || lang === "en") {
        clearCookie("googtrans");
        if (/translated-ltr|translated-rtl/.test(document.documentElement.className)) {
          window.location.reload();
        }
        return;
      }
      setCookie("googtrans", "/en/" + lang);
      var combo = document.querySelector(".goog-te-combo");
      if (combo) {
        combo.value = lang;
        combo.dispatchEvent(new Event("change"));
      } else {
        window.location.reload();
      }
    }

    /* ---- Keeping the toggle honest.

       Google Translate's real state lives in the googtrans cookie. This
       selector used to read localStorage only, which made the binding
       one-way: clicking an option updated both, but changing language in
       Google's OWN banner updated only the cookie. localStorage went
       stale and the toggle kept advertising a region the page was no
       longer translated into -- pick Japan, switch back to English in
       Google's bar, and it still read "JP | JA".

       The cookie is now the source of truth. localStorage survives only
       as a tie-breaker, because the mapping is many-to-one: US, CA-EN,
       UK, IE, AU, NZ and SG all resolve to "en". It is trusted ONLY while
       it still agrees with the cookie, so "CA | EN" doesn't collapse into
       "US | EN" on every reload, but a disagreement always resolves in
       the cookie's favour. */
    var lastTransLang;

    function readTransLang() {
      /* We write googtrans twice (path=/ and again with an explicit
         domain) and Google may write its own, so document.cookie can hold
         several. Last non-empty value wins; an absent/blank cookie means
         untranslated, i.e. English. */
      var parts = document.cookie.split(";");
      var raw = "";
      for (var i = 0; i < parts.length; i++) {
        var c = parts[i].trim();
        if (c.indexOf("googtrans=") === 0) {
          var v = decodeURIComponent(c.slice(10));
          if (v) raw = v;
        }
      }
      if (!raw) return "en";
      /* Value looks like "/en/ja" or "/auto/ja" -- target is the last segment. */
      var segs = raw.split("/").filter(Boolean);
      return segs.length ? segs[segs.length - 1] : "en";
    }

    function optionForLang(lang) {
      if (!regionList) return null;
      var opts = regionList.querySelectorAll("[data-region]");
      for (var i = 0; i < opts.length; i++) {
        if ((opts[i].getAttribute("data-lang") || "en") === lang) return opts[i];
      }
      return null;
    }

    function syncRegionToCookie() {
      var lang = readTransLang();
      var saved = null;
      try {
        saved = JSON.parse(window.localStorage.getItem(REGION_KEY) || "null");
      } catch (e) {}

      if (saved && saved.flag && saved.label && (saved.lang || "en") === lang) {
        applyRegion(saved.flag, saved.label);
        return;
      }

      /* Stored region contradicts the live translation (or there isn't
         one). Fall back to the first option for this language -- for "en"
         that's United States, which is the correct default. */
      var opt = optionForLang(lang);
      if (!opt) return;
      var flag = opt.getAttribute("data-flag");
      var label = opt.getAttribute("data-label");
      applyRegion(flag, label);
      try {
        window.localStorage.setItem(
          REGION_KEY,
          JSON.stringify({ flag: flag, label: label, lang: lang })
        );
      } catch (e) {}
    }

    syncRegionToCookie();
    lastTransLang = readTransLang();

    /* Google's banner renders in a cross-origin iframe, so there is no
       event we can subscribe to when the visitor changes language there.
       Poll the cookie instead: it is a string compare twice a second, and
       it catches every path -- the banner, the hidden .goog-te-combo,
       bfcache restores, and browser back/forward. */
    setInterval(function () {
      var now = readTransLang();
      if (now !== lastTransLang) {
        lastTransLang = now;
        syncRegionToCookie();
      }
    }, 500);

    window.addEventListener("pageshow", function () {
      lastTransLang = readTransLang();
      syncRegionToCookie();
    });

    if (regionToggle && regionList) {
      /* Class-based show/hide (.is-open), not the "hidden" attribute — the
         dropdown's own CSS needs an unconditional "display: flex" to lay
         out its columns, which permanently overrides the native [hidden]
         rule once set. A class toggle avoids that conflict entirely. */
      function closeRegionList() {
        regionList.classList.remove("is-open");
        regionToggle.setAttribute("aria-expanded", "false");
      }
      function openRegionList() {
        regionList.classList.add("is-open");
        regionToggle.setAttribute("aria-expanded", "true");
      }

      regionToggle.addEventListener("click", function (e) {
        e.stopPropagation();
        if (regionList.classList.contains("is-open")) {
          closeRegionList();
        } else {
          openRegionList();
        }
      });

      regionList.querySelectorAll("[data-region]").forEach(function (opt) {
        opt.addEventListener("click", function (e) {
          e.stopPropagation();
          var flag = opt.getAttribute("data-flag");
          var label = opt.getAttribute("data-label");
          var lang = opt.getAttribute("data-lang") || "en";
          applyRegion(flag, label);
          try {
            window.localStorage.setItem(REGION_KEY, JSON.stringify({ flag: flag, label: label, lang: lang }));
          } catch (e) {}
          /* Record the language we are about to set so the cookie poller
             treats this as an expected change and doesn't immediately
             re-derive the region (which would discard the specific choice
             just made -- e.g. collapsing "CA | EN" back to "US | EN"). */
          lastTransLang = lang;
          setSiteLanguage(lang);
          closeRegionList();
        });
      });

      document.addEventListener("click", function () {
        closeRegionList();
      });
    }
  }

  /* ---- Desktop mega-menu: JS-driven hover-intent. The panel now sits a
     deliberate ~14px below the header row (see --nav-gap in
     theme.css.liquid) instead of flush against it, which looks right but
     breaks pure-CSS :hover -- the hover chain is lost the instant the
     mouse crosses ANY pixel not covered by .nav-dropdown or one of its
     descendants, and that gap is exactly such a dead zone. A short
     close-delay bridges it: leaving the trigger (or the panel) doesn't
     close the menu instantly, only after a grace period a normal mouse
     path comfortably beats, and re-entering before it fires cancels the
     close. .is-open drives the same CSS the old :hover selectors did
     (kept alongside them, not replacing them, so nothing regresses if JS
     is ever unavailable). */
  var navDropdowns = document.querySelectorAll(".nav-dropdown");
  if (navDropdowns.length) {
    var navCloseTimer = null;
    var navOpenTimer = null;
    var NAV_CLOSE_DELAY_MS = 300;
    // Short open-delay too, separate from the close-delay above: a quick
    // mouse pass over a trigger (e.g. cutting across the nav toward
    // something else) no longer pops the panel open at all if the cursor
    // doesn't linger past this long.
    var NAV_OPEN_DELAY_MS = 150;

    var closeAllNavDropdowns = function () {
      navDropdowns.forEach(function (d) { d.classList.remove("is-open"); });
      if (header) header.classList.remove("is-menu-open");
    };
    var openNavDropdown = function (el) {
      clearTimeout(navCloseTimer);
      /* Publish the height of the panel about to open so the shared backdrop
         (.site-header::after) matches it exactly. Measured rather than
         hardcoded so the backdrop stays correct if a menu ever gains or
         loses a row. Read before the class changes, since the panel is laid
         out whether or not it is visible -- it's opacity/visibility that
         hide it, not display. */
      var openingPanel = el.querySelector(".nav-dropdown__panel");
      if (openingPanel && header) {
        header.style.setProperty("--nav-panel-h", openingPanel.offsetHeight + "px");
      }
      navDropdowns.forEach(function (d) {
        if (d !== el) d.classList.remove("is-open");
      });
      el.classList.add("is-open");
      /* Forces the header opaque while any mega-menu panel is open --
         matters specifically on Home at the very top of the page, where
         the header is otherwise still transparent (floating over the
         hero) and opening a dropdown would leave a strip of the hero
         photo visible between the transparent header row and the
         panel's own solid fill. See the .site-header.is-menu-open rules
         in theme.css.liquid, which also bridge the small gap below the
         row for exactly this state. Harmless everywhere else (header's
         already opaque there, or it's the same class the mobile menu
         toggle already uses). */
      if (header) header.classList.add("is-menu-open");
    };
    var scheduleNavOpen = function (el) {
      clearTimeout(navCloseTimer);
      clearTimeout(navOpenTimer);
      navOpenTimer = setTimeout(function () { openNavDropdown(el); }, NAV_OPEN_DELAY_MS);
    };
    var scheduleNavClose = function () {
      clearTimeout(navOpenTimer);
      clearTimeout(navCloseTimer);
      navCloseTimer = setTimeout(closeAllNavDropdowns, NAV_CLOSE_DELAY_MS);
    };

    navDropdowns.forEach(function (dropdown) {
      dropdown.addEventListener("mouseenter", function () { scheduleNavOpen(dropdown); });
      dropdown.addEventListener("mouseleave", scheduleNavClose);
      // Keyboard users get no open-delay (a Tab lands with intent, unlike
      // a mouse passing through), and the same forgiving close behavior
      // instead of the panel vanishing the instant focus moves.
      dropdown.addEventListener("focusin", function () { openNavDropdown(dropdown); });
      dropdown.addEventListener("focusout", function (e) {
        if (!dropdown.contains(e.relatedTarget)) scheduleNavClose();
      });
    });
  }

  /* ---- One orange item, enforced at the source.

     The rule has been "exactly one menu item is highlighted at a time"
     since the August pass, and the CSS side of it arbitrates between the
     three things that can each light a link: the page you are on, the link
     you pressed, and the press itself. This is a fourth source that the CSS
     cannot see, because it is not a conflict between states -- it is the
     SAME state applied twice.

     A parent whose own href equals one of its children's gets marked
     alongside that child. Shop points at /collections/all-products and so
     does All Products, so on that page Shopify emits aria-current="page" on
     both and two items go orange. Verified in the served markup: two
     aria-current links in the mobile panel, one Shop, one All Products.

     Keeping the LAST is the right call: a child always follows its parent
     in this markup, and the child is the more specific description of where
     you are. Only runs when there is genuinely more than one, so a parent
     that is legitimately current on its own page is untouched. */
  ["[data-mobile-panel]", ".site-header__nav", ".nav-dropdown__panel"].forEach(function (sel) {
    document.querySelectorAll(sel).forEach(function (scope) {
      var marked = scope.querySelectorAll('a[aria-current="page"]');
      if (marked.length < 2) return;
      for (var i = 0; i < marked.length - 1; i++) {
        marked[i].removeAttribute("aria-current");
      }
    });
  });

  /* ---- Mobile menu toggle ---- */
  var menuToggle = document.querySelector("[data-menu-toggle]");
  var mobilePanel = document.querySelector("[data-mobile-panel]");
  if (menuToggle && mobilePanel && header) {
    /* Scroll lock while the menu is open.

       Three separate symptoms collapse into this one fix. With the body
       still scrollable behind an open menu: the page slides around under
       it; the header flips to is-compact mid-menu, moving the panel's top
       edge; and on a phone the URL bar collapses, which changes 100dvh and
       resizes the panel underneath the finger. Freezing the body stops all
       three, and as a side effect makes dvh constant for the whole time
       the menu is open, which is the only reason it's a safe unit here.

       position:fixed rather than overflow:hidden, because iOS ignores
       overflow:hidden on body. The cost is that fixing the body scrolls it
       to the top, so the offset is stashed and restored on close --
       without that, closing the menu silently teleports you to the top of
       the page, which is a worse bug than the one being fixed. */
    var lockedScrollY = 0;
    function lockBodyScroll() {
      lockedScrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.position = "fixed";
      document.body.style.top = -lockedScrollY + "px";
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
    }
    function unlockBodyScroll() {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      // 'instant', not the default: a smooth restore animates the page back
      // under a menu that has already gone, which reads as a glitch.
      window.scrollTo({ top: lockedScrollY, behavior: "instant" });
    }

    menuToggle.addEventListener("click", function () {
      var open = mobilePanel.classList.toggle("is-open");
      header.classList.toggle("is-menu-open", open);
      if (open) { lockBodyScroll(); } else { unlockBodyScroll(); }
      menuToggle.querySelector(".icon-menu").style.display = open ? "none" : "";
      menuToggle.querySelector(".icon-close").style.display = open ? "" : "none";
      menuToggle.setAttribute("aria-label", open ? "Close menu" : "Open menu");
    });

    /* Orange-on-selection for the mobile nav.

       Why this can't just be CSS :active -- :active ends on finger-up,
       and finger-up happens long before the next page paints. The link
       would flash orange for ~80ms and then go white again while the
       browser is still fetching, so the tap reads as ignored. Adding a
       class on pointerdown and NOT removing it holds the color across
       that whole dead window; the navigation tears the DOM down for us.

       pointerdown, not click: it fires on contact rather than on release,
       so the feedback is immediate. Covers mouse, touch and pen in one
       listener. Delegated to the panel so links added by a merchant menu
       change are covered without rebinding.

       The previously-selected link is cleared first, otherwise scrolling
       the panel and pressing two links in a row (or a same-page anchor,
       where no navigation ever happens to clean up) leaves two lit. */
    mobilePanel.addEventListener("pointerdown", function (e) {
      var link = e.target.closest("a");
      if (!link || !mobilePanel.contains(link)) return;
      var prev = mobilePanel.querySelector("a.is-selected");
      if (prev && prev !== link) prev.classList.remove("is-selected");
      link.classList.add("is-selected");
      /* Hands the highlight to the pressed link and takes it back from the
         aria-current link for the page being left, so exactly one item is
         ever orange. Not removed on purpose -- same lifecycle as
         .is-selected; the navigation disposes of both. */
      mobilePanel.classList.add("is-navigating");
    });

    /* Restored from bfcache (Back button on iOS/Android), the DOM comes
       back exactly as it was left -- including the link we lit on the way
       out, now pointing at a page the user is no longer on. aria-current
       is the one that should be orange at that point, so drop ours. */
    window.addEventListener("pageshow", function (e) {
      if (!e.persisted) return;
      var lit = mobilePanel.querySelector("a.is-selected");
      if (lit) lit.classList.remove("is-selected");
      mobilePanel.classList.remove("is-navigating");
      // bfcache also restores the frozen <body>. Coming back to a page you
      // cannot scroll, with no menu open to explain why, is unrecoverable
      // without a reload -- so always release, menu state or not.
      if (document.body.style.position === "fixed") unlockBodyScroll();
    });

    /* Rotating to landscape or widening past 1280 hides the panel in CSS,
       which fires no event of its own. Without this the body stays frozen
       against a menu that is no longer on screen. */
    window.addEventListener("resize", function () {
      if (!mobilePanel.classList.contains("is-open")) return;
      if (getComputedStyle(mobilePanel).display !== "none") return;
      mobilePanel.classList.remove("is-open");
      header.classList.remove("is-menu-open");
      unlockBodyScroll();
    });
  }

  /* ---- Header search: click the icon to animate an input open to its
     left (icon itself stays put and just swaps to a close/X glyph --
     see .site-header__search rules in theme.css.liquid). Plain HTML
     form submit to Shopify's native /search results page, no
     predictive/AJAX search. Closes on: clicking the icon again while
     open, clicking anywhere outside the widget, or Escape. ---- */
  var searchWidget = document.querySelector("[data-search-widget]");
  if (searchWidget) {
    var searchToggle = searchWidget.querySelector("[data-search-toggle]");
    var searchInput = searchWidget.querySelector("[data-search-input]");

    function openSearch() {
      searchWidget.classList.add("is-active");
      /* Mirrored onto the header so the mobile rules can reach siblings the
         widget can't -- the logo and the wishlist/cart buttons live outside
         .site-header__search, and on a phone the field needs the whole row.
         See the is-search-open block in theme.css.liquid. */
      if (header) header.classList.add("is-search-open");
      searchToggle.setAttribute("aria-expanded", "true");
      // Wait a tick so the input isn't asked to focus while its parent
      // still has max-width:0 (some browsers silently ignore a focus()
      // call on a zero-width element instead of queuing it).
      window.requestAnimationFrame(function () {
        searchInput.focus();
      });
    }

    function closeSearch() {
      searchWidget.classList.remove("is-active");
      if (header) header.classList.remove("is-search-open");
      searchToggle.setAttribute("aria-expanded", "false");
    }

    searchToggle.addEventListener("click", function () {
      if (searchWidget.classList.contains("is-active")) {
        closeSearch();
      } else {
        openSearch();
      }
    });

    document.addEventListener("click", function (e) {
      if (searchWidget.classList.contains("is-active") && !searchWidget.contains(e.target)) {
        closeSearch();
      }
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && searchWidget.classList.contains("is-active")) {
        closeSearch();
        searchToggle.focus();
      }
    });

    /* ---- Predictive search: debounced fetch to Shopify's /search/suggest.json
       endpoint, rendered as a dropdown of product results under the input.
       Theme settings > Search controls whether this runs at all and how many
       results show; the plain form submit above still works as a fallback
       (Enter key, or clicking a result always lands on the real search page
       too via a "View all results" row). ---- */
    var resultsEl = searchWidget.querySelector("[data-search-results]");
    var predictiveEnabled = searchWidget.getAttribute("data-predictive-search") === "true";
    var predictiveLimit = parseInt(searchWidget.getAttribute("data-predictive-limit"), 10) || 5;

    if (predictiveEnabled && resultsEl) {
      var debounceTimer = null;
      var currentQuery = "";

      function renderResults(term, products) {
        if (!products.length) {
          resultsEl.innerHTML = '<p class="site-header__search-empty">No products found for "' + term + '"</p>';
          resultsEl.hidden = false;
          return;
        }
        var html = products
          .slice(0, predictiveLimit)
          .map(function (p) {
            var img = p.featured_image ? p.featured_image.url : p.image;
            return (
              '<a href="' + p.url + '" class="site-header__search-result">' +
              (img ? '<img src="' + img + '&width=100" alt="" loading="lazy">' : "") +
              '<span class="site-header__search-result-info">' +
              '<span class="site-header__search-result-title">' + p.title + "</span>" +
              '<span class="site-header__search-result-price">' + p.price + "</span>" +
              "</span></a>"
            );
          })
          .join("");
        html +=
          '<a href="/search?q=' + encodeURIComponent(term) + '&type=product" class="site-header__search-view-all">View all results for "' + term + '"</a>';
        resultsEl.innerHTML = html;
        resultsEl.hidden = false;
      }

      searchInput.addEventListener("input", function () {
        var term = searchInput.value.trim();
        currentQuery = term;
        window.clearTimeout(debounceTimer);
        if (term.length < 2) {
          resultsEl.hidden = true;
          resultsEl.innerHTML = "";
          return;
        }
        debounceTimer = window.setTimeout(function () {
          fetch("/search/suggest.json?q=" + encodeURIComponent(term) + "&resources[type]=product&resources[limit]=" + predictiveLimit + "&resources[options][unavailable_products]=last")
            .then(function (r) { return r.json(); })
            .then(function (data) {
              if (term !== currentQuery) return; // stale response, a newer keystroke already fired
              var products = (data.resources && data.resources.results && data.resources.results.products) || [];
              renderResults(term, products);
            })
            .catch(function () { resultsEl.hidden = true; });
        }, 220);
      });

      document.addEventListener("click", function (e) {
        if (!resultsEl.contains(e.target) && e.target !== searchInput) {
          resultsEl.hidden = true;
        }
      });
    }
  }

  /* ---- Wrap trailing "." in punctuation font ----
     Walks to the LAST TEXT NODE and only touches that, instead of reading
     el.textContent and overwriting the whole innerHTML from it (the old
     approach). That flattened-then-rebuilt method silently discarded any
     inline HTML -- bold/italic/links from a rich-text field -- earlier in
     the string. Now a heading like "Shift <strong>The</strong> Culture."
     keeps its bold intact and only the trailing period gets re-wrapped. */
  /* Terminal punctuation gets set in Soulmaze, per the Brand Guide.

     This used to match a trailing period ONLY, which meant any heading
     ending in "!" or "?" silently rendered its punctuation in the display
     face instead — the brand treatment applied or didn't depending on
     which mark a merchant happened to type. "You're In!" is exactly that
     case. All three terminal marks now qualify.

     Still deliberately last-character-only: mid-sentence periods
     (abbreviations, decimals, "Inc.") must not be restyled, and the
     ®/™ marks already carry their own handling. */
  var PUNCT_RE = /[.!?]$/;
  document.querySelectorAll(".js-punct").forEach(function (el) {
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
    var lastNode = null, node;
    while ((node = walker.nextNode())) { lastNode = node; }
    if (!lastNode) return;
    var text = lastNode.nodeValue;
    var trimmed = text.trim();
    if (!PUNCT_RE.test(trimmed)) return;
    var mark = trimmed.slice(-1);
    var span = document.createElement("span");
    span.className = "punct";
    span.textContent = mark;
    // Escape the mark for the regex -- "." and "?" are metacharacters.
    lastNode.nodeValue = text.replace(new RegExp("\\" + mark + "\\s*$"), "");
    lastNode.parentNode.insertBefore(span, lastNode.nextSibling);
  });

  /* ---- Hero banner carousel: auto cross-fades between up to 3
     merchant-configured banners, each with its own background image AND
     its own independent heading/text/button content (blocks/hero-
     banner.liquid) -- switching banners swaps the whole slide, not just
     the image. Dot indicators let a visitor jump straight to a specific
     promotion and see their position among them. Only runs when
     data-hero-carousel is present, which the section only renders once a
     2nd/3rd banner is added -- with a single banner (today's setup) this
     whole block is a no-op and the hero is a plain static section. ---- */
  var heroCarousel = document.querySelector("[data-hero-carousel]");
  if (heroCarousel) {
    var heroSlides = heroCarousel.querySelectorAll(".hero__slide");
    if (heroSlides.length > 1) {
      var heroDots = heroCarousel.querySelectorAll("[data-hero-dot]");
      var heroActiveIndex = 0;
      var heroSpeed = parseFloat(heroCarousel.getAttribute("data-hero-carousel-speed")) || 6;
      var heroTimer = null;

      /* Hand off from the CSS structural default (first .shopify-block
         wins -- see theme.css.liquid) to class-driven control before
         doing anything else, so slide 0 is explicitly .is-active the
         moment this script takes over and later rotation doesn't fight
         the structural rule for it. */
      heroCarousel.classList.add("hero--js-active");
      heroSlides[0].classList.add("is-active");
      if (heroDots[0]) heroDots[0].classList.add("is-active");

      function showHeroSlide(index) {
        heroSlides[heroActiveIndex].classList.remove("is-active");
        if (heroDots[heroActiveIndex]) heroDots[heroActiveIndex].classList.remove("is-active");
        heroActiveIndex = (index + heroSlides.length) % heroSlides.length;
        heroSlides[heroActiveIndex].classList.add("is-active");
        if (heroDots[heroActiveIndex]) heroDots[heroActiveIndex].classList.add("is-active");
      }

      function startHeroRotation() {
        heroTimer = setInterval(function () {
          showHeroSlide(heroActiveIndex + 1);
        }, heroSpeed * 1000);
      }

      function restartHeroRotation() {
        if (heroTimer) clearInterval(heroTimer);
        startHeroRotation();
      }

      startHeroRotation();

      heroDots.forEach(function (dot, i) {
        dot.addEventListener("click", function () {
          if (i === heroActiveIndex) return;
          showHeroSlide(i);
          restartHeroRotation();
        });
      });
    }
  }

  /* ---- Testimonials carousel: sliding window over up to 9 review cards
     (today's 9), 3 visible at a time on tablet/desktop (mobile stays the
     original stacked layout -- see the CSS media query). Chevrons only
     render (in Liquid) once there are more than 3 review-card blocks --
     with 3 or fewer this whole block is a no-op and the section is
     pixel-identical to the old static grid. Each chevron click pages by
     a full "visible" count (3) rather than one card at a time, so at the
     intended 9-card max it lands on exact page boundaries (0/3/6) with
     nothing partially revealed; maxIndex still clamps correctly if a
     merchant's card count isn't a clean multiple of 3. ---- */
  var testimonialsCarousel = document.querySelector("[data-testimonials-carousel]");
  if (testimonialsCarousel) {
    var testimonialsTrack = testimonialsCarousel.querySelector("[data-testimonials-track]");
    var testimonialsViewport = testimonialsCarousel.querySelector("[data-testimonials-viewport]");
    var testimonialsPrev = testimonialsCarousel.querySelector("[data-testimonials-prev]");
    var testimonialsNext = testimonialsCarousel.querySelector("[data-testimonials-next]");
    var testimonialsCards = testimonialsTrack ? Array.prototype.slice.call(testimonialsTrack.children) : [];

    if (testimonialsTrack && testimonialsViewport && testimonialsPrev && testimonialsNext && testimonialsCards.length > 3) {
      var testimonialsIndex = 0;

      function testimonialsStep() {
        var cardWidth = testimonialsCards[0].getBoundingClientRect().width;
        var gap = parseFloat(window.getComputedStyle(testimonialsTrack).columnGap) || parseFloat(window.getComputedStyle(testimonialsTrack).gap) || 0;
        return cardWidth + gap;
      }

      function testimonialsVisibleCount(step) {
        if (!step) return 1;
        return Math.max(1, Math.round(testimonialsViewport.getBoundingClientRect().width / step));
      }

      function updateTestimonials() {
        // Below the desktop breakpoint the track is a static vertical
        // stack (see CSS) -- clear any leftover transform from a wider
        // viewport and bail; chevrons are already display:none there.
        if (window.innerWidth < 768) {
          testimonialsTrack.style.transform = "";
          return;
        }
        var step = testimonialsStep();
        var visible = testimonialsVisibleCount(step);
        var maxIndex = Math.max(0, testimonialsCards.length - visible);
        testimonialsIndex = Math.min(testimonialsIndex, maxIndex);
        testimonialsTrack.style.transform = "translateX(-" + testimonialsIndex * step + "px)";
        testimonialsPrev.disabled = testimonialsIndex <= 0;
        testimonialsNext.disabled = testimonialsIndex >= maxIndex;
      }

      testimonialsPrev.addEventListener("click", function () {
        var step = testimonialsStep();
        var visible = testimonialsVisibleCount(step);
        testimonialsIndex = Math.max(0, testimonialsIndex - visible);
        updateTestimonials();
      });
      testimonialsNext.addEventListener("click", function () {
        var step = testimonialsStep();
        var visible = testimonialsVisibleCount(step);
        var maxIndex = Math.max(0, testimonialsCards.length - visible);
        testimonialsIndex = Math.min(maxIndex, testimonialsIndex + visible);
        updateTestimonials();
      });

      var testimonialsResizeTicking = false;
      window.addEventListener("resize", function () {
        if (testimonialsResizeTicking) return;
        testimonialsResizeTicking = true;
        window.requestAnimationFrame(function () {
          updateTestimonials();
          testimonialsResizeTicking = false;
        });
      });

      updateTestimonials();
    }
  }

  /* ---- Lightweight parallax for hero / visit-hq background images ---- */
  var parallaxEls = document.querySelectorAll('[data-parallax="true"] [data-parallax-img]');
  if (parallaxEls.length) {
    var pTicking = false;
    function onParallaxScroll() {
      parallaxEls.forEach(function (img) {
        var section = img.closest("[data-parallax]");
        var rect = section.getBoundingClientRect();
        var viewportH = window.innerHeight;
        var progress = (viewportH / 2 - (rect.top + rect.height / 2)) / (viewportH / 2 + rect.height / 2);
        var clamped = Math.max(-1, Math.min(1, progress));
        var offset = clamped * 60;
        var scale = img.hasAttribute("data-parallax-blur") ? " scale(1.19)" : "";
        img.style.transform = "translate3d(0," + offset + "px,0)" + scale;
      });
      pTicking = false;
    }
    window.addEventListener(
      "scroll",
      function () {
        if (!pTicking) {
          window.requestAnimationFrame(onParallaxScroll);
          pTicking = true;
        }
      },
      { passive: true }
    );
    onParallaxScroll();
  }

  /* ---- True edge-to-edge sizing for opt-in full-width image blocks ----
     .hx-image--full (theme.css.liquid) deliberately does NOT set its own
     width/margin -- an earlier pure-CSS calc(50% - 50vw) breakout there
     measurably didn't hold up nested inside a Flexible section's boxed
     inner wrapper (left edge landed short of the viewport), and layering
     this JS correction on top of that CSS compounded into an even-more-
     wrong result instead of fixing it. So the box is left in normal,
     unmodified boxed layout, and THIS is the only thing that ever sizes
     it: reset any of its own prior inline overrides first (so re-runs on
     resize measure a clean baseline, not their own last correction), then
     measure the element's real on-screen offset from its plain boxed
     position and cancel it out directly -- correct regardless of what's
     happening in the ancestor chain above it. */
  var fullBleedEls = document.querySelectorAll(".hx-image--full");
  if (fullBleedEls.length) {
    function sizeFullBleedImages() {
      fullBleedEls.forEach(function (el) {
        el.style.width = "";
        el.style.marginLeft = "";
        var rect = el.getBoundingClientRect();
        var viewportW = document.documentElement.clientWidth;
        el.style.width = viewportW + "px";
        el.style.marginLeft = -rect.left + "px";
      });
    }
    sizeFullBleedImages();
    var fbTicking = false;
    window.addEventListener(
      "resize",
      function () {
        if (fbTicking) return;
        fbTicking = true;
        window.requestAnimationFrame(function () {
          sizeFullBleedImages();
          fbTicking = false;
        });
      },
      { passive: true }
    );
  }
})();
